import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dropdown } from 'antd';
import styled from 'styled-components';

import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { canViewPricingData, hasCompanyAIOverviewFlag } from 'helpers/permissions';

import SidebarSelectorButton from './SidebarSelectorButton';
import SidebarSelectorDropdown from './SidebarSelectorDropdown';
import { ACTOR_TYPE } from '../../../../constants';
import { useAppDispatch, useAppSelector } from '../../../../store/hook';
import { handleSetSelectedCommunity, handleSetSelectedParentCommunity } from '../../../../store/slices/globalSlice';
import { useNavigationContext } from '../../Content/NavigationContext';
import { getUserSwitchRedirectPath, queueSupportModeRedirectPath, useUserSwitch } from '../../hooks';
import { SELECTOR_ITEM_TYPES } from '../constants';
import { SidebarContext } from '../SidebarContext';
import { SelectorItemDatatype } from '../types';

const Container = styled.div`
  position: relative;
  padding: 0px 10px 20px 10px;
`;

const SidebarSelector = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { closeMobileSidebar } = useNavigationContext();

  const { selectedCompany, selectedCommunity } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);
  const { handleUserSwitch } = useUserSwitch();
  const [open, setOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>(SELECTOR_ITEM_TYPES.ALL);
  const [filterKeyword, setFilterKeyword] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<SelectorItemDatatype>();

  useEffect(() => {
    if (selectedCommunity) {
      setSelectedItem({
        id: selectedCommunity.id,
        type:
          selectedCommunity.type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
            ? SELECTOR_ITEM_TYPES.COMMUNITY
            : SELECTOR_ITEM_TYPES.COMPETITOR,
        name: selectedCommunity.name,
      });
    } else if (selectedCompany) {
      setSelectedItem({
        id: selectedCompany.id,
        type: SELECTOR_ITEM_TYPES.COMPANY,
        name: selectedCompany.name,
      });
    } else {
      setSelectedItem(undefined);
    }
  }, [selectedCommunity, selectedCompany]);

  const handleItemSelected = (item: SelectorItemDatatype) => {
    if (!item.id) {
      console.error('Cannot handle sidebar selection: missing item id.', { item });
      setOpen(false);
      closeMobileSidebar();
      return;
    }

    if (ACTOR_TYPE.COMMUNITY === item.type) {
      navigate(`/community/${item.id}`);
    } else if (ACTOR_TYPE.COMPETITOR === item.type) {
      navigate(`/competitor/${item.id}`);
    } else if (item.type === ACTOR_TYPE.COMPANY) {
      // With the AI Company Overview flag, PrivateRoot decides the homepage because it needs the community list.
      const showPricingData = canViewPricingData(currentUser);
      const showAICompetitiveIntelligence =
        currentUser?.feature_flags?.show_ai_competitive_intelligence && !currentUser?.isSuperuser;
      const defaultCompanyHomepage =
        !showPricingData && showAICompetitiveIntelligence ? '/ai-competitive-intelligence' : '/overview';

      dispatch(handleSetSelectedCommunity(undefined));
      dispatch(handleSetSelectedParentCommunity(undefined));
      navigate(hasCompanyAIOverviewFlag(currentUser) ? '/' : defaultCompanyHomepage);
    } else if (item.type === ACTOR_TYPE.USER) {
      handleUserSwitch(item.id, (userInfo) => {
        const redirectPath = getUserSwitchRedirectPath(userInfo);
        queueSupportModeRedirectPath(redirectPath);
        setFilterType(SELECTOR_ITEM_TYPES.ALL);
        navigate(redirectPath);
      });
    }
    closeMobileSidebar();
    setOpen(false);
  };

  return (
    <SidebarContext.Provider
      value={{
        filterKeyword,
        filterType,
        setFilterType,
        setFilterKeyword,
        selectedItem,
        handleItemSelected,
        setOpen,
      }}
    >
      <Container>
        <Dropdown
          open={open}
          trigger={['click']}
          menu={{ items: [] }}
          destroyPopupOnHide
          onOpenChange={(flag) => {
            setOpen(flag);
          }}
          dropdownRender={(menu: any) => <SidebarSelectorDropdown />}
        >
          <div>
            <SidebarSelectorButton />
          </div>
        </Dropdown>
      </Container>
    </SidebarContext.Provider>
  );
};

export default SidebarSelector;
